import {
  ApPayeeFormInputContactAddress,
  ApPayeeFormInputContactDepartment,
  ApPayeeFormInputContactEmail,
  ApPayeeFormInputContactPersonName,
  ApPayeeFormInputContactPhoneNumber,
  ApPayeeFormInputOfficeMemberSelect,
} from '@/components/PayeeDetailForm';
import { SuspenseErrorBoundary } from '@/components/SuspenseErrorBoundary';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useSubmitPayeeInfo } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useSubmitPayeeInfo';
import {
  PICAndContactInformationForm,
  PICAndContractDetailData,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { checkIsServerError } from '@/features/errors/utils';
import { useTranslation } from '@/i18n';
import { formatPostalCode } from '@/utils/convert/formatPostalCode';
import {
  ButtonGroup,
  ButtonV2,
  Form,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import styles from '../../styles.module.scss';
import { HeaderSection, SingleRow } from '../Common';

const cx = classnames.bind(styles);

export type Props = {
  data?: PICAndContractDetailData | undefined;
};

export const Edit: FC<Props> = memo(({ data }) => {
  const { onEditFormDirtyChange, handleCancelEdit } = usePayeeDetailDrawer();
  const { t } = useTranslation();

  const defaultOptions = useMemo(() => {
    if (!data?.contact_office_members) return [];
    return data.contact_office_members.map((member) => ({
      value: member.id,
      label: member.display_name,
      caption: member.number,
    }));
  }, [data?.contact_office_members]);

  const defaultValues: PICAndContactInformationForm = useMemo(
    () => ({
      contact_office_member_ids:
        data?.contact_office_members?.map((m) => m.id) ?? [],
      contact_person_name: data?.contact_person_name ?? '',
      dept_name: data?.dept_name ?? '',
      contact_person_email: data?.contact_person_email ?? '',
      contact_person_phone_number: data?.contact_person_phone_number ?? '',
      postal_code: formatPostalCode(
        data?.ap_payee_contact_setting?.postal_code ?? ''
      ),
      prefecture_id: data?.ap_payee_contact_setting?.prefecture_id,
      address: data?.ap_payee_contact_setting?.address ?? '',
    }),
    [data]
  );

  const methods = useForm<PICAndContactInformationForm>({
    defaultValues,
    mode: 'all',
  });

  const { isValid, isDirty, isSubmitting } = useFormState({
    control: methods.control,
  });
  const { onSubmit, isLoading } =
    useSubmitPayeeInfo<PICAndContactInformationForm>({
      apPayeeId: data?.id ?? '',
    });

  useEffect(() => {
    if (!isSubmitting) return;
    onEditFormDirtyChange?.(false);
  }, [isSubmitting, onEditFormDirtyChange]);

  useEffect(() => {
    onEditFormDirtyChange?.(isDirty);
  }, [isDirty, onEditFormDirtyChange]);

  const handleCancel = useCallback(() => {
    handleCancelEdit?.(isDirty);
  }, [handleCancelEdit, isDirty]);

  return (
    <Form<PICAndContactInformationForm>
      formMethod={methods}
      onSubmit={onSubmit}
      testId='pic-and-contact-information-edit'
      className={cx('sectionContainer')}
    >
      <HeaderSection
        displayName={t('ap_payee_detail_drawer_pic_and_contact_information')}
        actionButton={
          <ButtonGroup direction='horizontal'>
            <ButtonV2
              color='primary'
              isSecondary
              onClick={handleCancel}
              type='button'
              isDisabled={isLoading}
              testId='cancel-button'
              containerClassName={cx('cancelButton')}
            >
              {t('cancel')}
            </ButtonV2>
            <ButtonV2
              color='primary'
              size='xs'
              type='submit'
              isTransparent={isLoading}
              isLoading={isLoading}
              isDisabled={!isValid || isLoading}
              testId='save-button'
              buttonContainerClassName={cx('saveButton')}
            >
              {t('save')}
            </ButtonV2>
          </ButtonGroup>
        }
      />
      <div className={cx('bodySectionContainer')}>
        <SingleRow
          label={t('ap_payee_contact_office_member_names')}
          renderContent={
            <SuspenseErrorBoundary
              shouldPropagate={checkIsServerError}
              fallback={<SuspenseLoading />}
            >
              <ApPayeeFormInputOfficeMemberSelect<PICAndContactInformationForm>
                control={methods.control}
                name='contact_office_member_ids'
                inputId='contact-office-members'
                defaultOptions={defaultOptions}
                required={false}
              />
            </SuspenseErrorBoundary>
          }
        />
        <SingleRow
          label={t('ap_payee_contact_person_name')}
          renderContent={
            <ApPayeeFormInputContactPersonName<PICAndContactInformationForm>
              control={methods.control}
              name='contact_person_name'
              inputId='contact-person-name'
              required={false}
            />
          }
        />
        <SingleRow
          label={t('ap_payee_contact_person_department')}
          renderContent={
            <ApPayeeFormInputContactDepartment<PICAndContactInformationForm>
              control={methods.control}
              name='dept_name'
              inputId='dept-name'
              required={false}
            />
          }
        />
        <SingleRow
          label={t('ap_payee_contact_person_email')}
          renderContent={
            <ApPayeeFormInputContactEmail<PICAndContactInformationForm>
              control={methods.control}
              name='contact_person_email'
              inputId='contact-person-email'
              required={false}
            />
          }
        />
        <SingleRow
          label={t('ap_payee_contact_person_phone_number')}
          renderContent={
            <ApPayeeFormInputContactPhoneNumber<PICAndContactInformationForm>
              control={methods.control}
              name='contact_person_phone_number'
              inputId='contact-person-phone-number'
              required={false}
            />
          }
        />
        <SingleRow
          label={t('ap_payee_contact_person_full_address')}
          renderContent={
            <ApPayeeFormInputContactAddress<PICAndContactInformationForm>
              control={methods.control}
              inputId='contact-address'
              postalCodeName='postal_code'
              prefectureIdName='prefecture_id'
              addressName='address'
              required={false}
            />
          }
        />
      </div>
    </Form>
  );
});

Edit.displayName = 'PICAndContactInformationEdit';
